import React from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../utils/styledComponent';
import Input from '../../components/atoms/forms/Input';
import NumberInput from '../../components/atoms/forms/NumberInput';
import FormField from '../../components/atoms/forms/FormField';
import DateTimeField from '../../components/atoms/forms/DateTimeField';
import Badge from '../../components/atoms/Badge';
import Button from '../../components/atoms/forms/Button';
import theme from '../../theme';

const StyledDiv = styledComponent('div', {
  '& .card': {
    border: theme.borders.card,
    borderRadius: theme.radii.inner,
    background: theme.colors.bgMuted,
    marginBottom: theme.spacing.fieldGap,
  },

  '& .header': {
    padding: theme.spacing.cellPadding,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: theme.borders.card,
  },

  '& .title': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.gapMd,
    ...theme.typography.body,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.text,
  },

  '& .fields': {
    padding: theme.spacing.fieldGap,
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: theme.spacing.fieldGap,
  },

  '& .vehicle-count': {
    padding: theme.spacing.cellPadding,
    borderTop: theme.borders.card,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.gapXl,
  },

  '& .vehicle-count-label': {
    ...theme.typography.label,
    color: theme.colors.text,
  },

  '& .vehicle-count-required': {
    color: theme.colors.danger,
    marginLeft: theme.spacing.requiredGap,
  },

  '& .vehicle-count-hint': {
    ...theme.typography.hint,
    color: theme.colors.textHint,
  },

  '@media (max-width: 1200px)': {
    '& .fields': {
      gridTemplateColumns: 'repeat(3, 1fr)',
    },
  },

  '@media (max-width: 900px)': {
    '& .fields': {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
  },

  '@media (max-width: 600px)': {
    '& .fields': {
      gridTemplateColumns: '1fr',
    },
  },
});

function handlePositiveIntChange(onChange, field, value) {
  if (value === '') {
    onChange(field, '');
    return;
  }
  const num = Number.parseInt(value, 10);
  if (Number.isNaN(num) || num < 1) return;
  onChange(field, String(num));
}

function formatDateTime(date) {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function VehicleTypeCard({
  vehicle,
  index,
  timezone,
  canRemove,
  onChange,
  onRemove,
}) {
  const shiftType = vehicle.vehicleType || 'AM';

  return (
    <StyledDiv>
      <div className="card">
        <div className="header">
          <div className="title">
            <span>Vehicle Type #{index + 1}</span>
            <Badge variant={shiftType.toLowerCase()}>{shiftType}</Badge>
          </div>
          <Button
            variant="danger-outline"
            onClick={onRemove}
            disabled={!canRemove}
          >
            Remove
          </Button>
        </div>

        <div className="fields">
          <FormField
            id={`capacity-${vehicle.id}`}
            label="Capacity"
            hint="API field: capacity"
            required
          >
            <Input
              id={`capacity-${vehicle.id}`}
              type="number"
              placeholder="e.g., 70"
              min="1"
              value={vehicle.capacity}
              onChange={(e) =>
                handlePositiveIntChange(onChange, 'capacity', e.target.value)
              }
            />
          </FormField>

          <FormField
            label="Shift Start"
            hint="API field: shift_start_time"
            required
          >
            <DateTimeField
              value={
                vehicle.shiftStartTime ? new Date(vehicle.shiftStartTime) : null
              }
              onChange={(val) =>
                onChange('shiftStartTime', formatDateTime(val))
              }
              minDate={new Date()}
            />
          </FormField>

          <FormField
            label="Target End"
            hint="API field: target_shift_end_time"
            required
          >
            <DateTimeField
              value={
                vehicle.targetShiftEndTime
                  ? new Date(vehicle.targetShiftEndTime)
                  : null
              }
              onChange={(val) =>
                onChange('targetShiftEndTime', formatDateTime(val))
              }
              minDate={new Date()}
            />
          </FormField>

          <FormField
            label="Shift End"
            hint="API field: shift_end_time"
            required
          >
            <DateTimeField
              value={
                vehicle.shiftEndTime ? new Date(vehicle.shiftEndTime) : null
              }
              onChange={(val) => onChange('shiftEndTime', formatDateTime(val))}
              minDate={new Date()}
            />
          </FormField>

          <FormField
            id={`lat-${vehicle.id}`}
            label="Latitude"
            hint="API field: lat"
            required
          >
            <Input
              id={`lat-${vehicle.id}`}
              type="number"
              step="0.000001"
              placeholder="e.g., 39.913467"
              value={vehicle.lat}
              onChange={(e) => onChange('lat', e.target.value)}
            />
          </FormField>

          <FormField
            id={`long-${vehicle.id}`}
            label="Longitude"
            hint="API field: long"
            required
          >
            <Input
              id={`long-${vehicle.id}`}
              type="number"
              step="0.000001"
              placeholder="e.g., -86.042816"
              value={vehicle.long}
              onChange={(e) => onChange('long', e.target.value)}
            />
          </FormField>

          <FormField
            id={`skill-${vehicle.id}`}
            label="Skill Set"
            hint="API field: skill_set"
          >
            <Input
              id={`skill-${vehicle.id}`}
              value={shiftType === 'AM' ? 'Daytime' : 'Overnight'}
              readOnly
            />
          </FormField>

          <FormField
            id={`timezone-${vehicle.id}`}
            label="Timezone"
            hint="API field: timezone"
          >
            <Input
              id={`timezone-${vehicle.id}`}
              value={timezone || ''}
              readOnly
            />
          </FormField>
        </div>

        <div className="vehicle-count">
          <label
            htmlFor={`num-vehicles-${vehicle.id}`}
            className="vehicle-count-label"
          >
            Number of Vehicles<span className="vehicle-count-required">*</span>
          </label>
          <NumberInput
            id={`num-vehicles-${vehicle.id}`}
            min="1"
            value={vehicle.numVehicles}
            onChange={(e) =>
              handlePositiveIntChange(onChange, 'numVehicles', e.target.value)
            }
          />
          <span className="vehicle-count-hint">vehicles of this type</span>
        </div>
      </div>
    </StyledDiv>
  );
}

VehicleTypeCard.propTypes = {
  vehicle: PropTypes.shape({
    id: PropTypes.string.isRequired,
    vehicleType: PropTypes.string,
    capacity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    shiftStartTime: PropTypes.string,
    targetShiftEndTime: PropTypes.string,
    shiftEndTime: PropTypes.string,
    lat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    long: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    numVehicles: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  index: PropTypes.number.isRequired,
  timezone: PropTypes.string,
  canRemove: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

VehicleTypeCard.defaultProps = {
  timezone: null,
  canRemove: false,
};

export default VehicleTypeCard;
